import type { LegendAnalyzerError } from '@/features/legends/legend-engine'
import type { SavedLegendVersion } from '@/features/legends/LegendStore'
import type { Validatable } from '@/features/legends/editor/validation'

/**
 * Analyzer errors for one path segment, with the errors of its children.
 * Array elements are keyed by index.
 */
export interface ErrorDescriptor {
  errors: string[]
  inner: ErrorDescriptorMap | null
}

export type ErrorDescriptorMap = Record<string | number, ErrorDescriptor>

/**
 * Something that knows how to deliver analyzer errors to its parts.
 */
export interface ErrorReceiver {
  receiveErrors(descriptor: ErrorDescriptor): void
}

/**
 * Adds a generic error for a nested property (a builder or an array) when the
 * descriptor has any errors.
 */
export function addGenericErrorForProperty(
  target: Validatable,
  descriptor: ErrorDescriptor | null | undefined,
  prop: string,
  message = 'There are validation errors.',
) {
  const hasErrors =
    !!descriptor &&
    ((!!descriptor.inner && Object.keys(descriptor.inner).length > 0) ||
      descriptor.errors.length > 0)
  if (hasErrors) {
    target.validation.addErrors({ [prop]: [message] })
  }
}

/**
 * Delivers errors to the target's properties, recursing into arrays and
 * anything with `receiveErrors`.
 */
export function receiveErrors(
  target: object,
  descriptorMap: ErrorDescriptorMap | null | undefined,
) {
  if (!descriptorMap) {
    return
  }

  const validation = hasValidation(target) ? target.validation : null
  for (const key of Object.keys(descriptorMap)) {
    const descriptor = descriptorMap[key]
    if (!descriptor) {
      continue
    }
    if (validation && descriptor.errors.length > 0) {
      validation.addErrors({ [key]: descriptor.errors })
    }
    const prop: unknown = Reflect.get(target, key)
    if (descriptor.inner && prop) {
      if (Array.isArray(prop)) {
        receiveErrors(prop, descriptor.inner)
      } else if (isErrorReceiver(prop)) {
        prop.receiveErrors(descriptor)
      }
    }
  }
}

/**
 * The analyzer errors of a save. Warnings are left out.
 *
 * @param saved
 */
export function getAnalyzerErrors(
  saved: SavedLegendVersion | null,
): LegendAnalyzerError[] {
  return (saved?.errors ?? []).filter((e) => e.severity === 'Error')
}

/**
 * The analyzer's errors with paths into the legend document instead of the
 * API's content: `stories[0].questions[2].inventory_item_id` becomes
 * `questions[2].inventoryItemId`.
 *
 * @param errors
 */
export function mapAnalyzerErrorsToDocumentPaths(
  errors: LegendAnalyzerError[],
): LegendAnalyzerError[] {
  return errors.map((error) => ({
    ...error,
    field: mapContentPathToDocumentPath(error.field),
  }))
}

/**
 * Turns flat `{ field: 'questions[0].title' }` errors into a tree of
 * descriptors.
 */
export function inflateAnalyzerErrors(
  errors: { field: string; message: string }[],
): ErrorDescriptorMap {
  const entries = new Map<
    string | number,
    { errors: string[]; pending: { field: string; message: string }[] }
  >()
  for (const error of errors) {
    const [segment, remaining] = getNextPathSegment(error.field)
    let entry = entries.get(segment)
    if (!entry) {
      entry = { errors: [], pending: [] }
      entries.set(segment, entry)
    }

    if (!remaining) {
      entry.errors.push(error.message)
    } else {
      entry.pending.push({ field: remaining, message: error.message })
    }
  }

  const result: ErrorDescriptorMap = {}
  for (const [key, entry] of entries) {
    result[key] = {
      errors: entry.errors,
      inner:
        entry.pending.length > 0 ? inflateAnalyzerErrors(entry.pending) : null,
    }
  }
  return result
}

/**
 * Splits off the first segment of an object path: a property name or an
 * array index.
 *
 *   "questions[0].title" => ["questions", "[0].title"]
 *   "[0].title" => [0, "title"]
 */
function getNextPathSegment(path: string): [string | number, string | null] {
  if (path.startsWith('[')) {
    const close = path.indexOf(']')
    const index = Number(path.substring(1, close))
    let rest = path.substring(close + 1)
    if (rest.startsWith('.')) {
      rest = rest.substring(1)
    }
    return [index, rest || null]
  }

  const start = path.startsWith('.') ? 1 : 0
  for (let i = start; i < path.length; i++) {
    const ch = path[i]
    if (ch === '.') {
      return [path.substring(start, i), path.substring(i)]
    }
    if (ch === '[') {
      return [path.substring(start, i), path.substring(i)]
    }
  }
  return [path.substring(start), null]
}

/**
 * Content field names whose document name isn't their camelCase.
 */
const documentFieldNames: Partial<Record<string, string>> = {
  collect_binding_id: 'bindingId',
  document_tags: 'documentTagIds',
  // The equality conditions of engine v0.9, which binary operations replaced,
  // hold the value under `eq`.
  eq: 'value',
  personal_organizer_collected_values: 'collectedValues',
  routing_context_data_source_inputs: 'dataSourceInputs',
  template_data_sources: 'dataSources',
}

function mapContentPathToDocumentPath(path: string) {
  // The content keeps the questions in the first story; the document has a
  // single list.
  const withQuestions = path.replace(/^stories\[0\]\.questions\b/, 'questions')
  // Only property names follow a dot or start the path; array indexes don't.
  return withQuestions.replace(
    /(^|\.)(\w+)/g,
    (_, separator: string, name: string) =>
      separator + (documentFieldNames[name] ?? snakeToCamelCase(name)),
  )
}

function snakeToCamelCase(name: string) {
  return /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/.test(name)
    ? name.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase())
    : name
}

function hasValidation(value: object): value is Validatable {
  return (
    'validation' in value &&
    typeof value.validation === 'object' &&
    value.validation !== null
  )
}

function isErrorReceiver(value: unknown): value is ErrorReceiver {
  return (
    typeof value === 'object' &&
    value !== null &&
    'receiveErrors' in value &&
    typeof value.receiveErrors === 'function'
  )
}
